// ========================================================
// ADMIN — USER MANAGEMENT TAB
// Enterprise identity / invite / family cleanup controls.
// Destructive Auth deletes go through /api/admin-users (service role).
// ========================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  Search,
  RefreshCw,
  Trash2,
  UserX,
  Mail,
  ShieldAlert,
  Users,
  Eye,
  AlertTriangle,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/SupabaseApiService';

const ApiService = apiService;

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '—';
  }
}

function ConfirmModal({
  open,
  title,
  description,
  confirmWord,
  onConfirm,
  onClose,
  loading,
  dangerLabel = 'Confirm'
}) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  if (!open) return null;

  const canConfirm = typed.trim() === confirmWord;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="rounded-full bg-red-100 p-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 id="confirm-modal-title" className="text-lg font-semibold text-slate-900">
              {title}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{description}</p>
          </div>
        </div>
        <label className="block text-xs font-medium text-slate-700">
          Type <span className="font-mono font-bold text-red-700">{confirmWord}</span> to confirm
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
            autoComplete="off"
            disabled={loading}
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm || loading}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
          >
            {loading ? 'Working…' : dangerLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const AdminUserManagement = () => {
  const [subTab, setSubTab] = useState('lookup'); // lookup | auth | members | invites | audit
  const [searchEmail, setSearchEmail] = useState('');
  const [lookup, setLookup] = useState(null);
  const [authUsers, setAuthUsers] = useState([]);
  const [authPage, setAuthPage] = useState(1);
  const [authHasMore, setAuthHasMore] = useState(false);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditWarning, setAuditWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const loadAuthPage = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const result = await ApiService.adminListAuthUsers({ page, perPage: 50 });
      if (!result.success) throw new Error(result.error);
      setAuthUsers(result.users || []);
      setAuthPage(page);
      setAuthHasMore(!!result.hasMore);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to load auth users');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ApiService.adminListMembers({ limit: 100 });
      if (!result.success) throw new Error(result.error);
      setMembers(result.members || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ApiService.adminListMembers({
        limit: 100,
        invitationStatus: 'pending'
      });
      if (!result.success) throw new Error(result.error);
      setInvites(result.members || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ApiService.adminListAudit({ limit: 50 });
      if (!result.success) throw new Error(result.error);
      setAudit(result.audit || []);
      setAuditWarning(result.warning || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'auth') loadAuthPage(1);
    if (subTab === 'members') loadMembers();
    if (subTab === 'invites') loadInvites();
    if (subTab === 'audit') loadAudit();
  }, [subTab, loadAuthPage, loadMembers, loadInvites, loadAudit]);

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const email = searchEmail.trim();
    if (!email) {
      toast.error('Enter an email to look up');
      return;
    }
    setLoading(true);
    setLookup(null);
    try {
      const result = await ApiService.adminLookupEmail(email);
      if (!result.success) throw new Error(result.error);
      setLookup(result);
      if (!result.authUser && !(result.familyMembers || []).length && !(result.ownedFamilies || []).length) {
        toast('No records found for that email', { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const runConfirmedAction = async () => {
    if (!confirmState) return;
    setActionLoading(true);
    try {
      const { kind, payload } = confirmState;
      let result;
      if (kind === 'purge_email') {
        result = await ApiService.adminPurgeEmail(payload.email);
      } else if (kind === 'delete_auth_user') {
        result = await ApiService.adminDeleteAuthUser(payload.userId, payload.email);
      } else if (kind === 'cancel_invite') {
        result = await ApiService.adminCancelInvite(payload.memberId);
      } else if (kind === 'remove_member') {
        result = await ApiService.adminRemoveMember(payload.memberId);
      } else if (kind === 'delete_family') {
        result = await ApiService.adminDeleteFamilyWithAuth(payload.familyId, payload.alsoDeleteOwnerAuth);
      } else {
        throw new Error('Unknown action');
      }
      if (!result.success) throw new Error(result.error);
      toast.success('Action completed');
      setConfirmState(null);
      if (lookup) await handleLookup();
      if (subTab === 'auth') await loadAuthPage(authPage);
      if (subTab === 'members') await loadMembers();
      if (subTab === 'invites') await loadInvites();
      if (subTab === 'audit') await loadAudit();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const subTabs = [
    { id: 'lookup', label: 'Email lookup' },
    { id: 'auth', label: 'Auth users' },
    { id: 'members', label: 'Family members' },
    { id: 'invites', label: 'Pending invites' },
    { id: 'audit', label: 'Audit log' }
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <div className="flex gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Destructive identity controls</p>
            <p className="mt-0.5 text-xs leading-relaxed">
              Auth user deletion and email purge run on the server with the service role and are audited.
              Prefer <strong>Purge email</strong> when freeing an address for retest (removes invites, owned family data, and Auth user).
              You cannot delete your own admin account.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSubTab(tab.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              subTab === tab.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'lookup' && (
        <div className="space-y-4">
          <form onSubmit={handleLookup} className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Lookup by email (e.g. admin@carekey.us)"
                className="w-full rounded-md border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Search className="mr-2 h-4 w-4" />
              Look up
            </button>
          </form>

          {lookup && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Users className="h-4 w-4" /> Auth user
                </h3>
                {lookup.authUser?.id ? (
                  <dl className="space-y-2 text-sm">
                    <div><dt className="text-slate-500">Email</dt><dd className="font-medium">{lookup.authUser.email || lookup.email || '—'}</dd></div>
                    <div><dt className="text-slate-500">User ID</dt><dd className="font-mono text-xs break-all">{lookup.authUser.id}</dd></div>
                    <div><dt className="text-slate-500">Confirmed</dt><dd>{formatDate(lookup.authUser.email_confirmed_at)}</dd></div>
                    <div><dt className="text-slate-500">Created</dt><dd>{formatDate(lookup.authUser.created_at)}</dd></div>
                    <div><dt className="text-slate-500">Last sign-in</dt><dd>{formatDate(lookup.authUser.last_sign_in_at)}</dd></div>
                    {lookup.profile?.role && (
                      <div><dt className="text-slate-500">Profile role</dt><dd className="font-medium">{lookup.profile.role}</dd></div>
                    )}
                  </dl>
                ) : (
                  <p className="text-sm text-slate-500">No Auth user for this email. Try Auth users tab, or redeploy if lookup RPC is missing.</p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {lookup.authUser?.id && (
                    <button
                      type="button"
                      onClick={() => setConfirmState({
                        kind: 'delete_auth_user',
                        confirmWord: 'DELETE',
                        title: 'Delete Auth user',
                        description: `Permanently delete Auth account ${lookup.authUser.email || lookup.email}. Family member rows are unlinked, not auto-purged.`,
                        payload: { userId: lookup.authUser.id, email: lookup.authUser.email || lookup.email }
                      })}
                      className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                    >
                      <UserX className="mr-1.5 h-3.5 w-3.5" />
                      Delete Auth user
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setConfirmState({
                      kind: 'purge_email',
                      confirmWord: 'PURGE',
                      title: 'Purge email everywhere',
                      description: `Delete owned families, all family_members rows, and Auth user for ${lookup.email}. This frees the email for a clean retest.`,
                      payload: { email: lookup.email }
                    })}
                    className="inline-flex items-center rounded-md bg-red-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-900"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Purge email
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-slate-900">Owned families</h3>
                  {(lookup.ownedFamilies || []).length === 0 ? (
                    <p className="text-sm text-slate-500">None</p>
                  ) : (
                    <ul className="space-y-2">
                      {lookup.ownedFamilies.map((f) => (
                        <li key={f.id} className="flex items-start justify-between gap-2 rounded border border-slate-100 p-2 text-sm">
                          <div>
                            <p className="font-medium">{f.family_name}</p>
                            <p className="text-xs text-slate-500 font-mono break-all">{f.id}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setConfirmState({
                              kind: 'delete_family',
                              confirmWord: 'DELETE',
                              title: 'Delete family + owner Auth',
                              description: `Delete family "${f.family_name}" and the owner Auth user.`,
                              payload: { familyId: f.id, alsoDeleteOwnerAuth: true }
                            })}
                            className="text-xs font-semibold text-red-700 hover:underline"
                          >
                            Delete
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="mb-3 text-sm font-semibold text-slate-900">Family member / invite rows</h3>
                  {(lookup.familyMembers || []).length === 0 ? (
                    <p className="text-sm text-slate-500">None</p>
                  ) : (
                    <ul className="space-y-2">
                      {lookup.familyMembers.map((m) => (
                        <li key={m.id} className="flex items-start justify-between gap-2 rounded border border-slate-100 p-2 text-sm">
                          <div>
                            <p className="font-medium">
                              {(m.first_name || '')} {(m.last_name || '')} · {m.role}
                            </p>
                            <p className="text-xs text-slate-500">
                              {m.invitation_status} · family {m.family_id?.slice(0, 8)}…
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setConfirmState({
                              kind: m.invitation_status === 'pending' ? 'cancel_invite' : 'remove_member',
                              confirmWord: 'DELETE',
                              title: m.invitation_status === 'pending' ? 'Cancel invitation' : 'Remove member',
                              description: `Remove ${m.email} (${m.role}) from family_members.`,
                              payload: { memberId: m.id }
                            })}
                            className="text-xs font-semibold text-red-700 hover:underline"
                          >
                            {m.invitation_status === 'pending' ? 'Cancel' : 'Remove'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {subTab === 'auth' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Auth users (page {authPage})</h3>
            <button
              type="button"
              onClick={() => loadAuthPage(authPage)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-slate-500">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-slate-500">Confirmed</th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-slate-500">Created</th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {authUsers.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-2">
                      <p className="font-medium text-slate-900">{u.email}</p>
                      <p className="font-mono text-xs text-slate-400">{u.id}</p>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{u.email_confirmed_at ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2 text-slate-600">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button
                        type="button"
                        onClick={async () => {
                          const email = u.email || '';
                          setSearchEmail(email);
                          setSubTab('lookup');
                          setLoading(true);
                          try {
                            const result = await ApiService.adminLookupEmail(email);
                            if (!result.success) throw new Error(result.error);
                            setLookup(result);
                          } catch (err) {
                            toast.error(err.message || 'Lookup failed');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="text-xs font-semibold text-blue-700 hover:underline"
                      >
                        <Eye className="inline h-3.5 w-3.5 mr-0.5" />
                        Inspect
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmState({
                          kind: 'delete_auth_user',
                          confirmWord: 'DELETE',
                          title: 'Delete Auth user',
                          description: `Delete ${u.email}`,
                          payload: { userId: u.id, email: u.email }
                        })}
                        className="text-xs font-semibold text-red-700 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {authUsers.length === 0 && !loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-slate-500">No users on this page.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between">
            <button
              type="button"
              disabled={authPage <= 1 || loading}
              onClick={() => loadAuthPage(authPage - 1)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!authHasMore || loading}
              onClick={() => loadAuthPage(authPage + 1)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {(subTab === 'members' || subTab === 'invites') && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">
              {subTab === 'invites' ? 'Pending invitations' : 'Family members'}
            </h3>
            <button
              type="button"
              onClick={() => (subTab === 'invites' ? loadInvites() : loadMembers())}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <ul className="divide-y divide-slate-100">
              {(subTab === 'invites' ? invites : members).map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-900">
                      <Mail className="mr-1 inline h-3.5 w-3.5 text-slate-400" />
                      {m.email}
                    </p>
                    <p className="text-xs text-slate-500">
                      {(m.first_name || '')} {(m.last_name || '')} · {m.role} · {m.invitation_status}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmState({
                      kind: m.invitation_status === 'pending' ? 'cancel_invite' : 'remove_member',
                      confirmWord: 'DELETE',
                      title: m.invitation_status === 'pending' ? 'Cancel invitation' : 'Remove member',
                      description: `Remove ${m.email} from family_members.`,
                      payload: { memberId: m.id }
                    })}
                    className="shrink-0 text-xs font-semibold text-red-700 hover:underline"
                  >
                    {m.invitation_status === 'pending' ? 'Cancel invite' : 'Remove'}
                  </button>
                </li>
              ))}
              {(subTab === 'invites' ? invites : members).length === 0 && !loading && (
                <li className="px-4 py-8 text-center text-sm text-slate-500">No rows.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {subTab === 'audit' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Admin audit log</h3>
            <button
              type="button"
              onClick={loadAudit}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          {auditWarning && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {auditWarning}
            </p>
          )}
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <ul className="divide-y divide-slate-100">
              {audit.map((row) => (
                <li key={row.id} className="px-4 py-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{row.action}</p>
                      <p className="text-xs text-slate-500">
                        by {row.admin_email || row.admin_user_id} → {row.target_email || row.target_id || '—'}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="h-3 w-3" />
                      {formatDate(row.created_at)}
                    </span>
                  </div>
                </li>
              ))}
              {audit.length === 0 && !loading && (
                <li className="px-4 py-8 text-center text-sm text-slate-500">No audit events yet.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {loading && (
        <p className="text-center text-sm text-slate-500">Loading…</p>
      )}

      <ConfirmModal
        open={!!confirmState}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmWord={confirmState?.confirmWord || 'DELETE'}
        onConfirm={runConfirmedAction}
        onClose={() => !actionLoading && setConfirmState(null)}
        loading={actionLoading}
        dangerLabel={confirmState?.kind === 'purge_email' ? 'Purge' : 'Delete'}
      />
    </div>
  );
};

export default AdminUserManagement;
